
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CheckCircle, XCircle, MessageSquare, Clock, User } from 'lucide-react';
import { useNodes, useUpdateNode } from '@/hooks/useNodes';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import NodeCard from './NodeCard';
import { toast } from 'sonner';
import type { Tables } from '@/integrations/supabase/types';

type Node = Tables<'nodes'> & {
  profiles?: {
    username: string | null;
    full_name: string | null;
  } | null;
  disciplines?: {
    name: string;
    slug: string;
  } | null;
};

const EditorWorkflow: React.FC = () => {
  const { canEditContent } = useRoleAccess();
  const { data: reviewNodes } = useNodes(undefined, 'in_review');
  const updateNodeMutation = useUpdateNode();
  
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject' | null>(null);
  const [feedback, setFeedback] = useState('');
  const [showReviewDialog, setShowReviewDialog] = useState(false);

  const handleReviewAction = (node: Node, action: 'approve' | 'reject') => {
    setSelectedNode(node);
    setReviewAction(action);
    setFeedback('');
    setShowReviewDialog(true);
  };

  const handleSubmitReview = async () => {
    if (!selectedNode || !reviewAction) return;

    try {
      const newStatus = reviewAction === 'approve' ? 'published' : 'draft';
      
      // Safely handle content update
      const currentContent = selectedNode.content && typeof selectedNode.content === 'object' 
        ? selectedNode.content as Record<string, any>
        : {};
      
      await updateNodeMutation.mutateAsync({
        id: selectedNode.id,
        updates: {
          status: newStatus,
          content: {
            ...currentContent,
            editorial_feedback: feedback || undefined
          }
        }
      });

      toast.success(
        reviewAction === 'approve' 
          ? 'Content approved and published' 
          : 'Content returned to draft with feedback'
      );
      
      setShowReviewDialog(false);
      setSelectedNode(null);
      setReviewAction(null);
      setFeedback('');
    } catch (error) {
      console.error('Error processing review:', error);
      toast.error('Failed to process review');
    }
  };

  if (!canEditContent) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
        <p className="text-black">
          You need editor access or higher to review content.
        </p>
      </div>
    );
  }

  const typedReviewNodes = reviewNodes as Node[] | undefined;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Editorial Review Queue</h2>
        <p className="text-black">
          Review and moderate content submitted by contributors
        </p>
      </div>

      {typedReviewNodes && typedReviewNodes.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {typedReviewNodes.map((node) => (
            <Card key={node.id} className="relative">
              <Badge className="absolute top-4 right-4" variant="secondary">
                <Clock className="h-3 w-3 mr-1" />
                In Review
              </Badge>
              
              <CardHeader className="pb-3">
                <CardTitle className="text-lg line-clamp-2">
                  {node.title}
                </CardTitle>
                <div className="flex items-center gap-2 text-sm text-black">
                  <User className="h-3 w-3" />
                  <span>By {node.profiles?.full_name || node.profiles?.username || 'Unknown'}</span>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <div className="text-sm text-black line-clamp-3">
                  {typeof node.content === 'object' && node.content && 'preview' in node.content 
                    ? String(node.content.preview)
                    : 'No preview available'}
                </div>
                
                <div className="flex items-center gap-2 text-xs text-black">
                  <Badge variant="outline">{node.node_type}</Badge>
                  <span>•</span>
                  <span>{node.disciplines?.name}</span>
                </div>
                
                <div className="flex gap-2 pt-2">
                  <Button
                    size="sm"
                    onClick={() => handleReviewAction(node, 'approve')}
                    disabled={updateNodeMutation.isPending}
                    className="flex-1"
                  >
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleReviewAction(node, 'reject')}
                    disabled={updateNodeMutation.isPending}
                    className="flex-1"
                  >
                    <XCircle className="h-3 w-3 mr-1" />
                    Reject
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Clock className="h-12 w-12 mx-auto text-black mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Content Under Review</h3>
          <p className="text-black">
            All submitted content has been reviewed. Check back later for new submissions.
          </p>
        </div>
      )}

      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reviewAction === 'approve' ? 'Approve Content' : 'Reject Content'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <h4 className="font-medium mb-2">{selectedNode?.title}</h4>
              <p className="text-sm text-black">
                By {selectedNode?.profiles?.full_name || selectedNode?.profiles?.username}
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">
                {reviewAction === 'approve' ? 'Approval Notes (Optional)' : 'Feedback for Contributor'}
              </label>
              <Textarea
                placeholder={
                  reviewAction === 'approve'
                    ? 'Add any notes about the approval...'
                    : 'Provide feedback to help the contributor improve their content...'
                }
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                className="min-h-[100px]"
              />
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowReviewDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitReview}
                disabled={updateNodeMutation.isPending}
                variant={reviewAction === 'approve' ? 'default' : 'destructive'}
              >
                {updateNodeMutation.isPending ? 'Processing...' : 
                 reviewAction === 'approve' ? 'Approve & Publish' : 'Reject & Return to Draft'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EditorWorkflow;
